import axios from "axios";

const API_URL =
  import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || "/api";

function normalizeApiUrl(url) {
  if (!url) return "/api";
  const trimmed = String(url).replace(/\/+$|\s+$/g, "");
  if (trimmed.includes("/api")) return trimmed;
  return `${trimmed}/api`;
}

// Create axios instance
const api = axios.create({
  baseURL: normalizeApiUrl(API_URL),
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message =
      error.response?.data?.error ||
      error.response?.data?.message ||
      "An error occurred";

    // Handle 401 - unauthorized
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("auth-storage");
      // Redirect to login if not already there
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }

    return Promise.reject({
      message,
      status: error.response?.status,
      response: error.response,
      original: error,
    });
  },
);

// Generic API helpers
export const apiGet = (url) => api.get(url).then((res) => res.data);
export const apiPost = (url, data) =>
  api.post(url, data).then((res) => res.data);
export const apiPut = (url, data) => api.put(url, data).then((res) => res.data);
export const apiPatch = (url, data) =>
  api.patch(url, data).then((res) => res.data);
export const apiDelete = (url) => api.delete(url).then((res) => res.data);

// Auth API
export const authAPI = {
  login: (data) => api.post("/auth/login", data),
  login2fa: (data) => api.post("/auth/login/2fa", data),
  requestEnable2fa: () => api.post("/auth/2fa/email/enable-request"),
  verifyEnable2fa: (data) => api.post("/auth/2fa/email/enable-verify", data),
  disable2fa: (data) => api.post("/auth/2fa/email/disable", data),
  getSecurity: () => api.get("/auth/security"),
  requestPasswordless: (data) => api.post("/auth/passwordless/request", data),
  verifyPasswordless: (data) => api.post("/auth/passwordless/verify", data),
  passkeyLoginOptions: (data) => api.post("/auth/passkeys/login/options", data),
  passkeyLoginVerify: (data) => api.post("/auth/passkeys/login/verify", data),
  passkeyRegisterOptions: () => api.post("/auth/passkeys/register/options"),
  passkeyRegisterVerify: (data) =>
    api.post("/auth/passkeys/register/verify", data),
  passkeyList: () => api.get("/auth/passkeys"),
  passkeyDelete: (credentialID) => api.delete(`/auth/passkeys/${credentialID}`),
  register: (data) => api.post("/auth/register", data),
  getMe: () => api.get("/auth/me"),
  updateProfile: (data) => api.put("/auth/me", data),
  changePassword: (data) => api.put("/auth/password", data),
  forgotPassword: (email, portal = "client") => api.post("/auth/forgot-password", { email, portal }),
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get("/users", { params }),
  getById: (id) => api.get(`/users/${id}`),
  invite: (data) => api.post("/users/invite", data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  // Recipient search for messaging (query param `q`)
  recipients: (q) => api.get("/users/recipients", { params: { q } }),
};

// Organizations API
export const organizationsAPI = {
  getAll: (params) => api.get("/organizations", { params }),
  getClients: () => api.get("/organizations/clients"),
  getById: (id) => api.get(`/organizations/${id}`),
  getMe: () => api.get("/organizations/me"),
  getMyFacilities: () => api.get("/organizations/me/facilities"),
  getFacilities: (id) => api.get(`/organizations/${id}/facilities`),
  getUsers: (id) => api.get(`/organizations/${id}/users`),
  getProjects: (id) => api.get(`/organizations/${id}/projects`),
  create: (data) => api.post("/organizations", data),
  update: (id, data) => api.put(`/organizations/${id}`, data),
  createFacility: (id, data) =>
    api.post(`/organizations/${id}/facilities`, data),
  updateFacility: (organizationId, facilityId, data) =>
    api.put(`/organizations/${organizationId}/facilities/${facilityId}`, data),
  delete: (id) => api.delete(`/organizations/${id}`),
  getServiceChangeRequests: (id, params) =>
    api.get(`/organizations/${id}/service-change-requests`, { params }),
  submitServiceChangeRequest: (id, data) =>
    api.post(`/organizations/${id}/service-change-requests`, data),
  orgDecideServiceChangeRequest: (requestId, data) =>
    api.post(`/organizations/service-change-requests/${requestId}/org-decision`, data),
};

// Projects API
export const projectsAPI = {
  getAll: (params) => api.get("/projects", { params }),
  getById: (id) => api.get(`/projects/${id}`),
  getActivity: (id, limit) =>
    api.get(`/projects/${id}/activity`, { params: { limit } }),
  getSummary: () => api.get("/projects/summary"),
  create: (data) => api.post("/projects", data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  updateTeam: (id, team) => api.put(`/projects/${id}/team`, { team }),
  updateContacts: (id, contacts) =>
    api.put(`/projects/${id}/contacts`, { clientContacts: contacts }),
  delete: (id) => api.delete(`/projects/${id}`),
  // Change requests
  createChangeRequest: (id, data) =>
    api.post(`/projects/${id}/change-requests`, data),
  getChangeRequests: (id) => api.get(`/projects/${id}/change-requests`),
};

// Documents API
export const documentsAPI = {
  getAll: (params) => api.get("/documents", { params }),
  getById: (id) => api.get(`/documents/${id}`),
  getDownloadUrl: (id, version) =>
    api.get(`/documents/${id}/download/${version || ""}`),
  create: (data) => api.post("/documents", data),
  requestUpload: (id, data) => api.post(`/documents/${id}/upload`, data),
  updateStatus: (id, data) => api.put(`/documents/${id}/status`, data),
  update: (id, data) => api.put(`/documents/${id}`, data),
  updateContent: (id, data) => api.put(`/documents/${id}/content`, data),
  saveSignature: (id, data) => api.post(`/documents/${id}/signature`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  // Comments
  getComments: (id, version) =>
    api.get(`/documents/${id}/comments`, { params: { version } }),
  addComment: (id, data) => api.post(`/documents/${id}/comments`, data),
  resolveComment: (docId, commentId) =>
    api.put(`/documents/${docId}/comments/${commentId}/resolve`),
};

// Messages API
export const messagesAPI = {
  getAll: (params) => api.get("/messages", { params }),
  getById: (id) => api.get(`/messages/${id}`),
  getUnreadCount: () => api.get("/messages/unread/count"),
  create: (data) => api.post("/messages", data),
  reply: (id, data) => api.post(`/messages/${id}/reply`, data),
  markAsRead: (id) => api.put(`/messages/${id}/read`),
  updateStatus: (id, status) => api.put(`/messages/${id}/status`, { status }),
};

// Invoices API
export const invoicesAPI = {
  getAll: (params) => api.get("/invoices", { params }),
  getBillingOverview: (params) =>
    api.get("/invoices/client/billing-overview", { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  getStats: (params) => api.get("/invoices/summary/stats", { params }),
  create: (data) => api.post("/invoices", data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  send: (id) => api.post(`/invoices/${id}/send`),
  addPayment: (id, data) => api.post(`/invoices/${id}/payment`, data),
  cancel: (id) => api.post(`/invoices/${id}/cancel`),
};

// Billing API (Stripe embedded flows)
export const billingAPI = {
  getPaymentMethods: () => api.get("/billing/payment-methods"),
  createSetupIntent: () => api.post("/billing/setup-intent"),
  createPaymentIntent: (data) => api.post("/billing/payment-intent", data),
};

// Uploads API
export const uploadsAPI = {
  getPresignedUrl: (data) => api.post("/uploads/presigned", data),
  uploadAvatar: (formData) =>
    api.post("/uploads/avatar", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  delete: (key) => api.delete("/uploads", { data: { key } }),
};

// LMS / Certificates API
export const certificatesAPI = {
  // User's certificates
  getAll: (params) => api.get("/lms/certificates", { params }),
  getById: (id) => api.get(`/lms/certificates/${id}`),
  // Download current or a specific revision (?revision=)
  download: (id, revision) =>
    api.get(`/lms/certificates/${id}/download`, { params: { revision } }),
  // Org level listing (admin)
  getOrg: (orgId, params) =>
    api.get(`/lms/certificates/org/${orgId}`, { params }),
};

export const learningPathsAPI = {
  getMy: () => api.get("/lms/paths/my"),
};

export const deadlineRequestsAPI = {
  getOrgSummary: (orgId, params) =>
    api.get(`/lms/reports/org/${orgId}/deadline-requests`, { params }),
  respond: (enrollmentId, requestId, data) =>
    api.post(`/lms/enrollments/${enrollmentId}/due-date-request/${requestId}/respond`, data),
};

export const riskQueueAPI = {
  getOrg: (orgId, params) =>
    api.get(`/lms/reports/org/${orgId}/risk-queue`, { params }),
  act: (orgId, data) =>
    api.post(`/lms/reports/org/${orgId}/risk-queue/action`, data),
  bulkAct: (orgId, data) =>
    api.post(`/lms/reports/org/${orgId}/risk-queue/bulk-action`, data),
};

// Staffing API
export const staffingAPI = {
  // Timesheets
  getTimesheets: (params) => api.get("/staffing/timesheets", { params }),
  getTimesheet: (id) => api.get(`/staffing/timesheets/${id}`),
  createTimesheet: (data) => api.post("/staffing/timesheets", data),
  updateTimesheet: (id, data) => api.put(`/staffing/timesheets/${id}`, data),
  submitTimesheet: (id, data) =>
    api.post(`/staffing/timesheets/${id}/submit`, data),
  approveTimesheet: (id, data) =>
    api.post(`/staffing/timesheets/${id}/client-approval`, data),
  submitWorkerRating: (id, data) =>
    api.post(`/staffing/timesheets/${id}/worker-rating`, data),

  // Staffing Requests
  getRequests: (params) => api.get("/staffing/requests", { params }),
  getRequest: (id) => api.get(`/staffing/requests/${id}`),
  createRequest: (data) => api.post("/staffing/requests", data),
  updateRequest: (id, data) => api.put(`/staffing/requests/${id}`, data),

  // Workers
  getWorkers: (params) => api.get("/staffing/workers", { params }),
  getWorker: (id) => api.get(`/staffing/workers/${id}`),

  // Shifts
  getShifts: (params) => api.get("/staffing/shifts", { params }),
  getShift: (id) => api.get(`/staffing/shifts/${id}`),

  // Facilities
  getFacilities: (params) => api.get("/staffing/facilities", { params }),
  getFacility: (id) => api.get(`/staffing/facilities/${id}`),
  updateFacility: (id, data) => api.put(`/staffing/facilities/${id}`, data),
};

export default api;
